import React, { useContext, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { captureEvent } from '../utils/posthogUtils';
import { NotificationsContext } from '../context/NotificationsContext';
import './NotificationsModal.css';

const typeToAccent = (type) => {
  switch (type) {
    case 'task_created':
      return '#1976d2';
    case 'task_updated':
      return '#7c5cff';
    case 'task_deleted':
      return '#e53935';
    case 'task_assigned':
      return '#2e7d32';
    default:
      return '#607d8b';
  }
};

export default function NotificationsModal() {
  const { notifications, modalOpen, closeModal, removeNotification, markAllRead, clearAll } = useContext(NotificationsContext);
  const posthog = usePostHog();

  const items = useMemo(() => notifications, [notifications]);
  if (!modalOpen) return null;

  const extractTaskName = (message) => {
    try {
      if (!message) return '';
      const m = message.match(/"([^"]+)"/);
      return m && m[1] ? m[1] : '';
    } catch { return ''; }
  };

  const buildMessage = (item) => {
    const rawTitle = item?.meta?.taskTitle || extractTaskName(item?.message) || '';
    const titleCaps = rawTitle ? rawTitle.toUpperCase() : '';
    const assigner = item?.meta?.assignerName || item?.meta?.assignedByName || item?.meta?.givenByName;
    const actor = item?.meta?.actorName || item?.meta?.updatedByName || item?.meta?.deletedByName;

    switch (item.type) {
      case 'task_assigned': {
        const who = assigner ? ` Assigned by ${assigner}.` : '';
        return (
          <>
            {titleCaps && (<span className="notif-task-name">{titleCaps}</span>)}
            {` has been assigned to you.`}
            {who}
          </>
        );
      }
      case 'task_created': {
        return (
          <>
            {titleCaps && (<span className="notif-task-name">{titleCaps}</span>)}
            {` has been created.`}
          </>
        );
      }
      case 'task_updated': {
        return item.message ? (
          <>
            {titleCaps && (<span className="notif-task-name">{titleCaps}</span>)}
            {` `}
            {item.message.replace(/"([^"]+)"/, '').trim()}
            {actor ? ` Updated by ${actor}.` : ''}
          </>
        ) : null;
      }
      case 'task_status_changed': {
        // Backend message already places the task name after "for ..."
        return (
          <>
            {item.message ? item.message : 'Status updated.'}
            {actor ? ` Updated by ${actor}.` : ''}
          </>
        );
      }
      case 'task_deleted': {
        return (
          <>
            {titleCaps && (<span className="notif-task-name">{titleCaps}</span>)}
            {` has been deleted.`}
            {actor ? ` Deleted by ${actor}.` : ''}
          </>
        );
      }
      default:
        return item.message || null;
    }
  };

  return (
    <div className="notif-overlay" onClick={closeModal}>
      <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notif-header">
          <div className="notif-title">Notifications</div>
          <div className="notif-actions">
            <button className="notif-btn ghost" onClick={() => { captureEvent(posthog, 'Notification Marked Read', { count: items.length }); markAllRead(); }}>Mark all read</button>
            <button className="notif-btn danger" onClick={() => { captureEvent(posthog, 'Notification Cleared', { count: items.length }); clearAll(); }}>Clear all</button>
            <button className="notif-btn" onClick={closeModal}>Close</button>
          </div>
        </div>
        <div className="notif-list">
          {items.length === 0 ? (
            <div className="notif-empty">You're all caught up ✨</div>
          ) : (
            items.map(item => (
              <div key={item.id} className={`notif-item${item.read ? ' read' : ''} ${item.type}`}>
                <div className="notif-accent" style={{ background: typeToAccent(item.type) }} />
                <div className="notif-content">
                  <div className="notif-row">
                    <div className="notif-item-title">{item.title}</div>
                    <div className="notif-time">{new Date(item.createdAt).toLocaleString()}</div>
                  </div>
                  <div className="notif-message">{buildMessage(item)}</div>
                </div>
                <button className="notif-remove" onClick={() => removeNotification(item.id)}>×</button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


